import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { State } from '../../typings/state';
import { TDispatchProps, TStateProps } from '../../typings/utils';
import {
    GALLERY_DEFAULT_CATEGORIES,
    GALLERY_DEFAULT_FETCH_FILTER_NAME,
    GALLERY_DEFAULT_FETCH_ORDER,
    Order,
    ResourceType
} from '../common/typings';
import {
    changeRecentNameFilter,
    changeRecentOrder,
    changeSelectedGalleryCategories,
    changeSemanticTextFilter,
    fetchGalleryCategoriesData,
    GalleryAction,
    previewVideoItem
} from '../GalleryPane/actions';
import { getCommonGalleryDispatchToProps } from '../GalleryPane/helpers';
import {
    selectCurrentGallery,
    selectEmptyGalleryPanesMap,
    selectRecentGalleryPane,
    selectStockGalleryPane
} from '../GalleryPane/selectors';
import { GalleryPaneTypes, PreviewVideoItemParams } from '../GalleryPane/typings';

import { CanvasVideoGallery, CanvasVideoGalleryProps } from './CanvasVideoGallery';

export type StateProps = TStateProps<CanvasVideoGalleryProps>;
export type DispatchProps = TDispatchProps<
    CanvasVideoGalleryProps & {
        dispatchSelectedGalleryCategories: (selectedGalleryCategoriesIdsList: string[]) => void;
    },
    {
        selectGalleryCategory: (selectedCategoryId: string) => void;
    }
>;

const mapDispatchToProps = (dispatch: Dispatch<GalleryAction>): DispatchProps => {
    const commonDispatchProps = getCommonGalleryDispatchToProps(dispatch);

    return {
        ...commonDispatchProps,

        previewVideoItem: (paneType: GalleryPaneTypes, params?: PreviewVideoItemParams): void => {
            dispatch(previewVideoItem(paneType, params));
        },

        // фильтры
        changeSemanticTextFilter: (filter: string) => {
            dispatch(changeSemanticTextFilter(filter));
        },
        changeRecentOrder: (order: Order) => {
            dispatch(changeRecentOrder(order));
        },
        changeRecentNameFilter: (filter: string) => {
            dispatch(changeRecentNameFilter(filter));
        },
        fetchGalleryCategoriesData: () => {
            dispatch(fetchGalleryCategoriesData());
        },
        dispatchSelectedGalleryCategories: (selectedGalleryCategoriesIdsList: string[]) => {
            dispatch(changeSelectedGalleryCategories(selectedGalleryCategoriesIdsList));
        }
    };
};

export const mapStateToProps = (state: State): StateProps => {
    const resourceType = ResourceType.video;
    const { galleries } = state.canvasEditor.canvas;
    const { isPopupOpened, videoStockCategoriesData, resources, currentResourceType, error } = galleries;
    const resource = resources[resourceType];

    const { currentPaneType, enabledGalleryTypes, preview, isBusy } = selectCurrentGallery(galleries);
    const { data } = resource.panes[currentPaneType];

    let semanticText = GALLERY_DEFAULT_FETCH_FILTER_NAME;
    let selectedGalleryCategories = GALLERY_DEFAULT_CATEGORIES;
    let recentOrder = GALLERY_DEFAULT_FETCH_ORDER;
    let recentNameFilter = GALLERY_DEFAULT_FETCH_FILTER_NAME;

    if (enabledGalleryTypes.includes(GalleryPaneTypes.stock)) {
        const stockFilters = selectStockGalleryPane(galleries).filters;
        semanticText = stockFilters.semanticText;
        selectedGalleryCategories = stockFilters.selectedGalleryCategories;
    }

    if (enabledGalleryTypes.includes(GalleryPaneTypes.recent)) {
        const recentFilters = selectRecentGalleryPane(galleries).filters;

        recentOrder = recentFilters.recentOrder;
        recentNameFilter = recentFilters.recentNameFilter;
    }

    return {
        isBusy,
        currentPaneType,
        isPopupOpened: isPopupOpened && currentResourceType === ResourceType.video,
        enabledGalleryTypes,
        error,
        emptyGalleriesMap: selectEmptyGalleryPanesMap(galleries, resourceType),
        preview,
        categoriesData: videoStockCategoriesData,
        recentOrder,
        semanticText,
        recentNameFilter,
        selectedGalleryCategories,
        currentPaneCollection: data.collection
    };
};

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps) => {
    const { selectedGalleryCategories } = stateProps;
    const { dispatchSelectedGalleryCategories } = dispatchProps;

    return {
        ...stateProps,
        ...dispatchProps,
        selectGalleryCategory: (selectedId: string) => {
            const result = Array().concat(selectedGalleryCategories);

            const indexOfSelectedId = selectedGalleryCategories.indexOf(selectedId);

            if (indexOfSelectedId === -1) {
                result.push(selectedId);
            } else {
                result.splice(indexOfSelectedId, 1);
            }
            dispatchSelectedGalleryCategories(result);
        }
    };
};

export const CanvasVideoGalleryContainer = connect<StateProps, DispatchProps, {}, CanvasVideoGalleryProps>(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
)(CanvasVideoGallery);
